import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router'
import { Button } from '../ui'
import { fetchMe } from '../lib/api'
import './magic-link.css'

type Phase = 'checking' | 'unavailable'

/**
 * The application root, `/` — a gate, not a page.
 *
 * The app had no route for `/` at all, so every `window.location.assign('/')` fell through to the
 * catch-all and was sent to `/auth/sign-in`: the workspace chooser after it attaches one (see
 * ConnectTenant), the magic-link "Continue", a bookmark to the bare domain. For a session that had
 * JUST chosen a workspace that meant signing in, choosing, and arriving back on the sign-in page —
 * and since the sign-in page does not turn a signed-in visitor away, clicking sign-in again
 * repeated the whole trip. That is the loop a customer belonging to more than one workspace hit.
 *
 * So `/` asks the server who this is and sends them where they belong. It renders nothing lasting;
 * a signed-in customer sees it for one `/me` round trip.
 */
export function Home() {
  const navigate = useNavigate()
  const [phase, setPhase] = useState<Phase>('checking')

  useEffect(() => {
    let cancelled = false

    void (async () => {
      const result = await fetchMe()
      if (cancelled) return

      // A transient failure is not a signed-out visitor. Bouncing a 500 or a dropped request to
      // sign-in is the same over-collapse the API client warns about — and here it would recreate
      // the very loop this gate exists to end. So a real failure stops on a retry, and only a 401
      // (which fetchMe returns as ok:true with null data) sends anyone to sign-in.
      if (!result.ok) {
        setPhase('unavailable')
        return
      }

      if (!result.data) {
        navigate('/auth/sign-in', { replace: true })
        return
      }

      // The server says where setup is; the client renders that rather than its own memory of
      // which screen it was on. A session with a workspace goes to it; one without goes to the
      // screen that resolves that — the chooser for someone who belongs to several or was invited,
      // the connect screen for a genuinely new customer. Both live at /connect.
      navigate(
        result.data.setupState === 'has_workspace' ? '/dashboard' : '/connect',
        { replace: true },
      )
    })()

    return () => {
      cancelled = true
    }
  }, [navigate])

  return (
    <main className="magic-link rise-in">
      <div className="label-mono">Defenssive AI Security Manager</div>

      {phase === 'checking' ? (
        <>
          <h2>One moment…</h2>
          <p className="text-muted">Taking you to the right place.</p>
        </>
      ) : (
        <>
          <h2>We could not load your account just now</h2>
          <p className="text-muted">
            Something on our side did not answer. Your sign-in is unaffected — try again in a
            moment.
          </p>
          <Button variant="primary" onClick={() => window.location.reload()}>
            Try again
          </Button>
        </>
      )}
    </main>
  )
}
